import { useCallback, useState } from 'react';
import { streamQuery } from '../api';
import { DEMO_QUESTIONS } from '../demoQuestions';
import type { DemoQuestion, DemoResult } from '../demoQuestions';
import type { Citation, Presentation, StreamEvent } from '../types';

interface DemoRow {
  demo: DemoQuestion;
  status: 'pending' | 'running' | 'pass' | 'fail';
  result?: DemoResult;
  failReason?: string;
}

export function Demo() {
  const [rows, setRows] = useState<DemoRow[]>(
    DEMO_QUESTIONS.map((d) => ({ demo: d, status: 'pending' })),
  );
  const [running, setRunning] = useState(false);

  const runOne = useCallback(async (demo: DemoQuestion): Promise<DemoRow> => {
    const t0 = performance.now();
    let prose = '';
    let presentation: Presentation = { type: 'prose' };
    let citations: Citation[] = [];
    const toolsUsed: string[] = [];
    let error: string | undefined;

    await streamQuery({ query: demo.query }, {
      onEvent: (ev: StreamEvent) => {
        if (ev.type === 'token') {
          prose += (ev.data as { text: string }).text;
        } else if (ev.type === 'tool_call') {
          toolsUsed.push((ev.data as { tool_key: string }).tool_key);
        } else if (ev.type === 'presentation') {
          const d = ev.data as { presentation: Presentation; citations: Citation[] };
          presentation = d.presentation;
          citations = d.citations;
        } else if (ev.type === 'error') {
          error = (ev.data as { message: string }).message;
        }
      },
      onError: (e) => { error = e.message; },
    });

    const latencyMs = Math.round(performance.now() - t0);
    const result: DemoResult = { prose, presentation, citations, toolsUsed, latencyMs, error };
    const failReason = error ?? demo.check(result) ?? null;
    return {
      demo,
      status: failReason ? 'fail' : 'pass',
      result,
      failReason: failReason ?? undefined,
    };
  }, []);

  const runAll = useCallback(async () => {
    setRunning(true);
    setRows(DEMO_QUESTIONS.map((d) => ({ demo: d, status: 'pending' })));
    for (const demo of DEMO_QUESTIONS) {
      setRows((prev) => prev.map((r) => r.demo.id === demo.id ? { ...r, status: 'running' } : r));
      const result = await runOne(demo);
      setRows((prev) => prev.map((r) => r.demo.id === demo.id ? result : r));
    }
    setRunning(false);
  }, [runOne]);

  const copyReport = useCallback(() => {
    const md = ['# HRSA HDW Conversational Search — Demo Report', '',
      `Run at: ${new Date().toISOString()}`,
      `Pass: ${rows.filter((r) => r.status === 'pass').length} / ${rows.length}`, '',
      '| Q | Status | Latency | Tools | Notes |',
      '|---|--------|---------|-------|-------|',
      ...rows.map((r) => {
        const tools = r.result?.toolsUsed.join(', ') ?? '';
        const lat = r.result?.latencyMs ?? '';
        const notes = r.failReason ?? r.result?.prose.slice(0, 80) ?? '';
        return `| ${r.demo.id} | ${r.status === 'pass' ? '✓' : r.status === 'fail' ? '✗' : '…'} | ${lat}ms | ${tools} | ${notes} |`;
      }),
    ].join('\n');
    navigator.clipboard.writeText(md).catch(() => {});
  }, [rows]);

  const passCount = rows.filter((r) => r.status === 'pass').length;
  const failCount = rows.filter((r) => r.status === 'fail').length;
  const partialCount = DEMO_QUESTIONS.filter((d) => d.partialDataCoverage).length;

  return (
    <div className="demo-page">
      <h1>Demo Suite — {DEMO_QUESTIONS.length} POC Questions</h1>
      <p className="demo-subtitle">
        Questions sourced from the HDW Demo Sample Questions spreadsheet. Each
        row's check validates the tools used, the presentation type, and the
        prose content. Burns Anthropic credits — runs sequentially.
      </p>
      {partialCount > 0 && (
        <p className="demo-subtitle demo-coverage-note">
          ⓘ {partialCount} of {DEMO_QUESTIONS.length} questions reference UDS / MIECHV bulk-data files
          that aren't wired into the tool layer yet (see{' '}
          <code>src/demoQuestions.ts</code>). Their checks accept either an
          ArcGIS-backed partial answer or an explicit acknowledgment of the
          data gap from the agent.
        </p>
      )}
      <div className="demo-actions">
        <button onClick={runAll} disabled={running} className="btn-primary">
          {running ? 'Running…' : 'Run all'}
        </button>
        <button onClick={copyReport} disabled={running || passCount + failCount === 0}>
          Copy as report
        </button>
        <span className="demo-summary">
          Pass: {passCount} / {rows.length}{failCount > 0 ? ` (✗ ${failCount})` : ''}
        </span>
      </div>
      <table className="demo-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Query</th>
            <th>Status</th>
            <th>Latency</th>
            <th>Tools used</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.demo.id} className={`status-${r.status}`}>
              <td>{r.demo.id}</td>
              <td title={r.demo.purpose}>
                {r.demo.shortLabel}
                {r.demo.partialDataCoverage && (
                  <span className="demo-partial-badge" title="Partial dataset coverage">
                    {' '}◐
                  </span>
                )}
              </td>
              <td>
                {r.status === 'pass' && '✓ pass'}
                {r.status === 'fail' && '✗ fail'}
                {r.status === 'running' && '…'}
                {r.status === 'pending' && '·'}
              </td>
              <td>{r.result?.latencyMs ?? ''}{r.result ? 'ms' : ''}</td>
              <td>{r.result?.toolsUsed.join(', ') ?? ''}</td>
              <td>{r.failReason ?? r.result?.prose.slice(0, 80) ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
